import { Box, Divider, Typography } from "@mui/material";
import { ReactElement } from "react";
import React from "react";

import CollapsibleDrawer from "~frontend/components/CollapsibleDrawer";
import { FlexBox } from "~frontend/components/FlexBox";
import { Format, formatters } from "~frontend/components/stats/NumberStat";
import { palette } from "~frontend/theme";

import { StatsQueryResult } from "./useCampaignStats";

type StatsRow = {
  label: string;
  value: number;
  format: Format;
  children: {
    label: string;
    value: number;
    format: Format;
  }[];
};

function formatRatio(
  numerator: number | undefined,
  denominator: number | undefined
): number {
  return numerator && denominator && denominator > 0
    ? numerator / denominator
    : 0;
}

function StatsTable({ rows }: { rows: StatsRow[] }): ReactElement {
  return (
    <>
      <Divider sx={{ borderColor: palette.lightGray }} />
      <Box sx={{ px: 3 }}>
        {rows.map(row => (
          <React.Fragment key={row.label}>
            <FlexBox justifyContent="space-between" sx={{ mt: 1.5 }}>
              <Typography variant="body2Bold">{row.label}</Typography>
              <Typography variant="body2Bold">
                {formatters[row.format](row.value, 2)}
              </Typography>
            </FlexBox>
            {row.children.map(child => (
              <FlexBox
                key={child.label}
                justifyContent="space-between"
                sx={{ pt: 0.5 }}
              >
                <Typography variant="body3">{child.label}</Typography>
                <Typography variant="body3">
                  {formatters[child.format](child.value, 2)}
                </Typography>
              </FlexBox>
            ))}
            <Divider sx={{ mt: 1.5, borderColor: palette.lightGray }} />
          </React.Fragment>
        ))}
      </Box>
    </>
  );
}

export default function DetailStatsDrawer({
  stats,
  showClicks,
  showFundraisingFields,
}: {
  stats: StatsQueryResult;
  showClicks: boolean;
  showFundraisingFields: boolean;
}): ReactElement | null {
  const rows: StatsRow[] = [];

  if (stats.state != "loaded") {
    return null;
  }

  rows.push({
    label: "Total Sent",
    format: "number",
    value: stats.data.contactCount,
    children: [],
  });

  if (showFundraisingFields) {
    const {
      donationCount,
      recurringDonationCount,
      totalRaisedInCents,
      totalRecurringRaisedInCents,
      totalRefundedInCents,
    } = stats.data;

    rows.push(
      {
        label: "Number of Donations",
        format: "number",
        value: donationCount,
        children: [
          {
            label: "Recurring Donations",
            format: "number",
            value: recurringDonationCount,
          },
        ],
      },
      {
        label: "Total Raised",
        format: "usd",
        value: totalRaisedInCents / 100,
        children: [
          {
            label: "Recurring Donations Raised",
            format: "usd",
            value: totalRecurringRaisedInCents / 100,
          },
          {
            label: "Average Donation",
            format: "usd",
            value: formatRatio(totalRaisedInCents, donationCount) / 100,
          },
          {
            label: "Total Refunds",
            format: "usd",
            // If refunds occurred, display them with a negative sign.
            value: totalRefundedInCents > 0 ? totalRefundedInCents / -100 : 0,
          },
        ],
      }
    );
  }

  if (showClicks) {
    rows.push({
      label: "Clicks",
      format: "number",
      value: stats.data.clickCount,
      children: showFundraisingFields
        ? [
            {
              label: "Conversion (Click to Donation)",
              format: "percent",
              value: formatRatio(
                stats.data.donationCount,
                stats.data.clickCount
              ),
            },
          ]
        : [],
    });
  }

  if (stats.data.__typename === "CampaignStatsForDetail") {
    // top-level campaign stats; show both initial and total cost
    rows.push({
      label: "Estimated Cost",
      format: "usd",
      value: stats.data.totalCostInMillicents / (100 * 1000),
      children: [
        {
          label: "Initial Message Cost",
          format: "usd",
          value: stats.data.initialCostInMillicents / (100 * 1000),
        },
      ],
    });
  } else {
    // variant stats; show initial cost only
    rows.push({
      label: "Initial Message Cost",
      format: "usd",
      value: stats.data.initialCostInMillicents / (100 * 1000),
      children: [],
    });
  }

  rows.push({
    label: "Opt Outs",
    format: "number",
    value: stats.data.contactsOptedOutCount,
    children: [
      {
        label: "Automatic Opt Outs",
        value: stats.data.contactsAutomaticOptedOutCount,
        format: "number",
      },
    ],
  });

  return (
    <CollapsibleDrawer
      variant="slim"
      title=""
      expandText="More Results"
      collapseText="Less Results"
      triggerSx={{
        marginY: 1,
      }}
      startOpen={false}
    >
      <StatsTable rows={rows} />
    </CollapsibleDrawer>
  );
}
